import { duplicateMeshFbxAsset } from './MeshFbx';
import { duplicateMeshPlyAsset } from './MeshPly';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { duplicateMeshObjAsset } from './MeshObj';
import * as Three from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import toast from 'react-hot-toast';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import { setLoadingMessage } from '../../redux/Slice/globalStates';
import {
  add_new_assets,
  addModel,
  addSceneMesh,
  selected_object,
  set_is_glb_image_editoropen,
  setPushIntersectedObject,
  update_assets_data,
} from '../../redux/Slice/gridSlice';
import store from '../../redux/store';
import { getNextCopyName, buildTempFileName } from '../../utils/NamingUtils';
import {
  UpdateMaterial,
  colorToAlbedoHex,
  cloneMaterialSettingsForDuplicate,
  mergeDuplicateSourceItem,
  applyDuplicateMaterialToObject,
  applyMaterialSettingsAfterLoad,
  applyRenderView,
  applyMapScalarsFromSettings,
} from './materialFunction';
import { applyChildMaterialOverrides, resolveChildMaterialOverrides, applyWireframeMeshIndices, relinkMeshesBySharedMaterialId } from '../../utils/ThreeJsUtils/materialPropertyEditor';
import {
  hasMaterialModifications,
  hasSkinnedMeshInHierarchy,
  hasStoredSurfaceMaterialEdits,
  isolateDuplicatePublishIdentity,
  resolveGltfExportRoot,
  isPivotEditedAsset,
  hierarchyHasPivotEditFlag,
} from '../../pages/Training/utils/gridpageUtils';
import { attachClonedEventsToDuplicate } from '../../utils/eventAssetUtils';
import {
  MAP_BACKUP_USERDATA_KEY,
  MAP_PREVIEW_URLS_USERDATA_KEY,
  MAP_VISIBILITY_USERDATA_KEY,
  objectHasEmbeddedGltfMaps,
  restoreMapsFromGltfUserData,
  restoreMaterialMapsFromSettings,
  setMaterialMapVisibility,
  syncMaterialMapBackupsFromSlots,
} from '../../utils/ThreeJsUtils/materialMaps';
import {
  applyMapScalars,
  EDITOR_MAP_SCALARS_KEY,
} from '../../utils/ThreeJsUtils/materialPanelSchema';
import { registerGltfSkinJointSanitizer } from '../../utils/ThreeJsUtils/sanitizeGltfJsonForLoader.js';
import {
  applyShapeMetadataToDuplicate,
  detachNestedShapeAssetsForExport,
  findMeshItemForSceneObject,
  getChildShapeAssetsFromMeshData,
  getNestedSceneMeshChildren,
  reattachDetachedShapeAssets,
  removeBakedChildShapesFromLoadedModel,
  syncNestedChildrenSceneParent,
} from '../GridAssets/shapeAssetUtils';
import {
  isolateGlbAnimationMixers,
  stopPlayModelForObject,
} from '../AddComponents/AnimationComponent/handlers/animationEffectHandlers';
import { beginSuppressedMeshAddHistory } from '../../utils/gridHistoryDiscrete';

/**
 * Module-level registry: maps server-assigned Mongo ID → library identity.
 * Populated by GridPage after publish (when the temp→Mongo ID remap is known).
 * Consumed by DownloadMesh so that source/from_library survive the scene reload
 * even when the API doesn't echo those fields back in its response.
 */
const _libraryIdentityRegistry = new Map();

export function registerLibraryIdentity(mongoId, identity) {
  if (!mongoId) return;
  _libraryIdentityRegistry.set(mongoId, identity);
}

export function getLibraryIdentity(mongoId) {
  return mongoId ? _libraryIdentityRegistry.get(mongoId) : undefined;
}

/**
 * Keeps animated GLB skeletons intact: bones/skinned meshes must keep matrixAutoUpdate on.
 * Never call SkinnedMesh.pose() during playback — it breaks skinning.
 */
function ensureSkinnedGlbReady(root) {
  if (!root) return;
  root.traverse((child) => {
    child.matrixAutoUpdate = true;
    if (child.isBone || child.isSkinnedMesh) {
      child.matrixAutoUpdate = true;
    }
    if (child.isSkinnedMesh && child.skeleton) {
      child.skeleton.bones?.forEach((bone) => {
        bone.matrixAutoUpdate = true;
      });
    }
  });
}

/** Editor GLB wrapper: pivot Group > loaded gltf.scene (matches DownloadMesh / publish). */
function resolveGlbPivotFromScene(scene, item, originalMesh) {
  const candidateIds = [
    item?.temp_file_name,
    item?.material_settings?.id,
    item?.userData?.material_settings?.id,
    item?._id,
    item?.url,
    item?.name,
    item?.id,
  ].filter(Boolean);

  let source =
    originalMesh ||
    (typeof item?.clone === 'function' ? item : null);

  if (!source && scene) {
    for (const id of candidateIds) {
      source = scene.getObjectByName(id);
      if (source) break;
    }
    if (!source && candidateIds.length > 0) {
      scene.traverse((obj) => {
        if (!source && candidateIds.includes(obj.name)) source = obj;
      });
    }
  }

  if (!source) return null;
  if (source.deleteType === 'mesh' && source.objType === '3d') return source;

  let node = source;
  while (node?.parent && node.parent !== scene) {
    if (node.parent.deleteType === 'mesh' && node.parent.objType === '3d') {
      return node.parent;
    }
    node = node.parent;
  }
  return source;
}

/** Three.js clone() copies UUIDs — duplicated GLB subtrees need unique ids for selection/hierarchy. */
function assignFreshObjectUuids(root, Three) {
  if (!root?.traverse) return;
  root.traverse((obj) => {
    obj.uuid = Three.MathUtils.generateUUID();
  });
}

// Three.Object3D.copy deep-copies userData via JSON.stringify; these runtime refs
// are circular (mixer -> root -> object -> userData) and must be stripped before clone.
const NON_CLONEABLE_USERDATA_KEYS = [
  'mixer',
  'animationActions',
  'animationClips',
  'activeAction',
  'activeClip',
  'activePlayModelEffect',
  'animationMixer',
];

function stripNonCloneableUserDataRuntime(root) {
  const backup = new Map();
  if (!root?.traverse) return backup;
  root.traverse((obj) => {
    if (!obj?.userData || typeof obj.userData !== 'object') return;
    let saved = null;
    NON_CLONEABLE_USERDATA_KEYS.forEach((key) => {
      if (obj.userData[key] !== undefined) {
        if (!saved) saved = {};
        saved[key] = obj.userData[key];
        delete obj.userData[key];
      }
    });
    if (saved) backup.set(obj, saved);
  });
  return backup;
}

function restoreNonCloneableUserDataRuntime(backup) {
  backup?.forEach((saved, obj) => {
    if (!obj) return;
    obj.userData = obj.userData || {};
    Object.assign(obj.userData, saved);
  });
}

/**
 * clone() runs after animationClips are stripped (circular mixer refs).
 * Copy clips from the restored source onto the duplicate by traverse order.
 */
function copyAnimationDataFromSource(sourceRoot, targetRoot) {
  if (!sourceRoot?.traverse || !targetRoot?.traverse) return;

  const sourceNodes = [];
  const targetNodes = [];
  sourceRoot.traverse((obj) => sourceNodes.push(obj));
  targetRoot.traverse((obj) => targetNodes.push(obj));

  const count = Math.min(sourceNodes.length, targetNodes.length);
  for (let i = 0; i < count; i++) {
    const src = sourceNodes[i];
    const dst = targetNodes[i];
    if (!src || !dst) continue;

    const clips = src.userData?.animationClips;
    if (Array.isArray(clips) && clips.length > 0) {
      dst.userData = dst.userData || {};
      dst.userData.animationClips = clips.map((clip) =>
        clip && typeof clip.clone === 'function' ? clip.clone() : clip
      );
    }

    if (src.isAnimationActive !== undefined) {
      dst.isAnimationActive = src.isAnimationActive;
    }
  }

  if (sourceRoot.isAnimationActive !== undefined) {
    targetRoot.isAnimationActive = sourceRoot.isAnimationActive;
  }
}

function resolveDuplicateSourceFile(sourcePivot, item, meshInScene = null) {
  const candidates = [
    sourcePivot?.originalFile,
    sourcePivot?.localFile,
    sourcePivot?.userData?.duplicateSourceFile,
    meshInScene?.originalFile,
    meshInScene?.localFile,
    meshInScene?.userData?.duplicateSourceFile,
    item?.originalFile,
    item?.localFile,
    item?.userData?.duplicateSourceFile,
  ];
  for (const file of candidates) {
    if (file instanceof File && file.size > 0) return file;
  }
  return null;
}

/** Prefer original upload bytes over CDN path (rigged GLBs must not use broken re-exports). */
function resolveDuplicateGlbLoadUrl(item, sourcePivot) {
  const file = resolveDuplicateSourceFile(sourcePivot, item);
  if (file) {
    return { url: URL.createObjectURL(file), revoke: true, sourceFile: file };
  }
  if (item?.asset_file_path) {
    return { url: item.asset_file_path, revoke: false, sourceFile: null };
  }
  return null;
}

/**
 * Build pivot + centered model like initial scene load.
 * Never geometry.translate() on skinned meshes — that breaks skeleton binding.
 */
function createGlbPivotFromGltfScene(gltf, Three, options = {}) {
  const {
    pivotId,
    position,
    rotation,
    scale,
    isAnimationActive,
    preserveAuthoredPivot = false,
  } = options;

  const model = gltf.scene;
  ensureSkinnedGlbReady(model);

  const pivot = new Three.Group();
  pivot.name = pivotId;
  pivot._id = pivotId;
  pivot.url = pivotId;
  pivot.deleteType = 'mesh';
  pivot.objType = '3d';
  if (isAnimationActive !== undefined) {
    pivot.isAnimationActive = isAnimationActive;
  }

  const shouldPreserve =
    preserveAuthoredPivot || hierarchyHasPivotEditFlag(model);
  if (!shouldPreserve) {
    const box = new Three.Box3().setFromObject(model);
    const worldCenter = box.getCenter(new Three.Vector3());
    model.position.sub(worldCenter);
  }
  pivot.add(model);
  model.updateMatrixWorld(true);

  if (shouldPreserve) {
    pivot.userData = pivot.userData || {};
    pivot.userData.pivotEdited = true;
    pivot.pivotEdited = true;
  }

  if (Array.isArray(gltf.animations) && gltf.animations.length > 0) {
    try {
      const mixer = new Three.AnimationMixer(model);
      model.userData = model.userData || {};
      model.userData.mixer = mixer;
      model.userData.animationActions = [];
      model.userData.animationClips = gltf.animations;
      gltf.animations.forEach((clip) => {
        try {
          const action = mixer.clipAction(clip);
          action.reset();
          action.setLoop(Three.LoopRepeat);
          model.userData.animationActions.push(action);
        } catch (e) {
          console.warn('Failed to create animation action', e);
        }
      });
      ensureSkinnedGlbReady(model);
    } catch (e) {
      console.warn('Failed to initialize animations for duplicated GLB', e);
    }
  }

  pivot.position.set(position?.x ?? 0, position?.y ?? 0, position?.z ?? 0);
  if (rotation) pivot.rotation.copy(rotation);
  if (scale) pivot.scale.copy(scale);
  pivot.updateMatrixWorld(true);

  pivot.userData = pivot.userData || {};
  pivot.userData.hasSkinnedMesh = hasSkinnedMeshInHierarchy(model);

  return { pivot, model };
}

function collectGlbMeshSlots(root) {
  const slots = [];
  if (!root?.traverse) return slots;
  root.traverse((obj) => {
    if (obj.isMesh || obj.isSkinnedMesh) slots.push(obj);
  });
  return slots;
}

function glbMeshSlotKey(mesh, root = null) {
  const parts = [];
  let node = mesh;
  while (node && node !== root) {
    parts.unshift(node.name || node.uuid);
    node = node.parent;
  }
  return parts.join('/');
}

function resolveGlbMeshSlot(mesh, root, meshByKey, meshByName) {
  const relativeKey = glbMeshSlotKey(mesh, root);
  if (relativeKey && meshByKey.has(relativeKey)) {
    return meshByKey.get(relativeKey);
  }
  const simpleName = mesh.name || mesh.uuid;
  if (simpleName && meshByName.has(simpleName)) {
    return meshByName.get(simpleName);
  }
  return null;
}

/** Copy per-mesh materials from the source GLB in the scene (keeps glass, wood, etc.). */
function copyGlbMaterialsFromSource(sourceRoot, targetRoot) {
  const sourceByKey = new Map();
  const sourceByName = new Map();
  collectGlbMeshSlots(sourceRoot).forEach((mesh) => {
    const relativeKey = glbMeshSlotKey(mesh, sourceRoot);
    if (relativeKey) sourceByKey.set(relativeKey, mesh);
    const simpleName = mesh.name || mesh.uuid;
    if (simpleName && !sourceByName.has(simpleName)) {
      sourceByName.set(simpleName, mesh);
    }
  });

  collectGlbMeshSlots(targetRoot).forEach((targetMesh) => {
    const sourceMesh = resolveGlbMeshSlot(
      targetMesh,
      targetRoot,
      sourceByKey,
      sourceByName
    );
    if (!sourceMesh?.material) return;

    if (Array.isArray(sourceMesh.material)) {
      targetMesh.material = sourceMesh.material.map(cloneMaterialWithEditorState);
    } else {
      targetMesh.material = cloneMaterialWithEditorState(sourceMesh.material);
    }

    if (sourceMesh.userData?.threeMaterialEdited) {
      targetMesh.userData = targetMesh.userData || {};
      targetMesh.userData.threeMaterialEdited = true;
    }
  });
}

function cloneMaterialWithEditorState(material) {
  if (!material?.clone) return material;
  const cloned = material.clone();
  if (material.userData && typeof material.userData === 'object') {
    cloned.userData = { ...material.userData };
    if (material.userData.originalMaterialProps) {
      cloned.userData.originalMaterialProps = {
        ...material.userData.originalMaterialProps,
      };
    }
    if (material.userData[MAP_VISIBILITY_USERDATA_KEY]) {
      cloned.userData[MAP_VISIBILITY_USERDATA_KEY] = {
        ...material.userData[MAP_VISIBILITY_USERDATA_KEY],
      };
    }
    if (material.userData[MAP_PREVIEW_URLS_USERDATA_KEY]) {
      cloned.userData[MAP_PREVIEW_URLS_USERDATA_KEY] = {
        ...material.userData[MAP_PREVIEW_URLS_USERDATA_KEY],
      };
    }
    if (material.userData[MAP_BACKUP_USERDATA_KEY]) {
      cloned.userData[MAP_BACKUP_USERDATA_KEY] = {
        ...material.userData[MAP_BACKUP_USERDATA_KEY],
      };
    }
  }
  cloned.needsUpdate = true;
  return cloned;
}

/**
 * Match DownloadMesh: preserve file materials; only apply API overrides when needed.
 * When duplicating, prefer live materials copied from sourcePivot.
 */
function applyDuplicateGlbMaterials({
  gltfModel,
  pivot,
  sourcePivot,
  item,
  Three,
}) {
  applyChildMaterialsAfterGltfLoad(gltfModel);
  syncMaterialMapBackupsFromSlots(gltfModel);
  restoreMapsFromGltfUserData(gltfModel);

  const materialSettings =
    sourcePivot?.userData?.material_settings ||
    item?.material_settings ||
    null;

  if (materialSettings && typeof materialSettings === 'object') {
    pivot.userData = pivot.userData || {};
    pivot.userData.material_settings = cloneMaterialSettingsForDuplicate(
      sourcePivot,
      materialSettings
    );
  }

  const sourceInner = sourcePivot
    ? resolveGltfExportRoot(sourcePivot)
    : null;

  if (sourceInner && sourceInner !== pivot) {
    copyGlbMaterialsFromSource(sourceInner, gltfModel);
    syncMaterialMapBackupsFromSlots(gltfModel);
    const childOverrides =
      resolveChildMaterialOverrides(pivot.userData?.material_settings) ||
      resolveChildMaterialOverrides(sourcePivot?.userData?.material_settings);
    if (childOverrides) {
      applyChildMaterialOverrides(pivot, childOverrides);
    }
    const wireIdx =
      pivot.userData?.material_settings?.wireframe_mesh_indices ||
      sourcePivot?.userData?.material_settings?.wireframe_mesh_indices;
    if (wireIdx) applyWireframeMeshIndices(pivot, wireIdx);
    return;
  }

  if (
    materialSettings?.mapVisibility &&
    !objectHasEmbeddedGltfMaps(gltfModel)
  ) {
    Object.entries(materialSettings.mapVisibility).forEach(([mapKey, visible]) => {
      if (typeof visible === 'boolean') {
        setMaterialMapVisibility(gltfModel, mapKey, visible);
      }
    });
  }

  const glbType = item?.glb_type || 'normal';
  const legacyMaterialOverrides = hasMaterialModifications(materialSettings);
  const needsUnlitEmissionTexture =
    legacyMaterialOverrides &&
    materialSettings?.shader === 'Unlit' &&
    materialSettings?.textureImage;

  if (needsUnlitEmissionTexture) {
    gltfModel.traverse((child) => {
      if (
        child.isMesh &&
        child.material?.isMeshBasicMaterial &&
        materialSettings.textureImage
      ) {
        new Three.TextureLoader().load(materialSettings.textureImage, (tex) => {
          child.material.map = tex;
          child.material.needsUpdate = true;
        });
      }
    });
  } else if (
    materialSettings &&
    (legacyMaterialOverrides ||
      glbType === 'shapes' ||
      hasStoredSurfaceMaterialEdits(materialSettings)) &&
    glbType !== 'normal' &&
    glbType !== ''
  ) {
    applyMaterialSettingsAfterLoad(pivot, gltfModel, materialSettings);
  }
}

/**
 * After GLTFLoader: fix maps/colorSpace without overwriting materials via UpdateMaterial.
 * Many GLBs use baseColorFactor black + texture — without white vertex color they render black.
 */
function fixGltfMaterialsAfterLoad(material) {
  if (!material) return;

  material.needsUpdate = true;

  const setMap = (tex, colorSpace) => {
    if (!tex) return;
    tex.needsUpdate = true;
    tex.flipY = false;
    if (colorSpace) tex.colorSpace = colorSpace;
  };

  setMap(material.map, Three.SRGBColorSpace);
  setMap(material.emissiveMap, Three.SRGBColorSpace);
  setMap(material.normalMap, Three.LinearSRGBColorSpace);
  setMap(material.roughnessMap, Three.LinearSRGBColorSpace);
  setMap(material.metalnessMap, Three.LinearSRGBColorSpace);
  setMap(material.aoMap, Three.LinearSRGBColorSpace);
  setMap(material.alphaMap, Three.LinearSRGBColorSpace);

  if (material.map && material.color) {
    const hex = material.color.getHex();
    if (hex === 0x000000) {
      material.color.set(0xffffff);
    }
  }

  if (material.emissiveMap && material.emissive) {
    const eHex = material.emissive.getHex();
    if (eHex === 0x000000) {
      material.emissive.set(0xffffff);
    }
  }

  if (material.metalnessMap && material.metalness === undefined) {
    material.metalness = 1;
  }
  if (material.roughnessMap && material.roughness === undefined) {
    material.roughness = 1;
  }
}

/**
 * After GLTFLoader: keep each child mesh's opacity / transparent / depthWrite from the file.
 * Upload path already uses preserveMaterial(); DownloadMesh must do the same.
 */
function syncGltfMaterialSurfaceAfterLoad(material) {
  if (!material) return;

  fixGltfMaterialsAfterLoad(material);

  const opacity =
    typeof material.opacity === 'number' ? material.opacity : 1;
  const alphaTest =
    typeof material.alphaTest === 'number' ? material.alphaTest : 0;
  const clampedOpacity = Math.max(0, Math.min(1, opacity));

  material.opacity = clampedOpacity;

  const needsTransparency =
    clampedOpacity < 1 - 1e-6 ||
    alphaTest > 0 ||
    !!material.alphaMap ||
    material.userData?.editorNeedsTransparency === true ||
    // Keep GLTF BLEND materials (transparent already true, opacity may be 1).
    material.transparent === true;

  if (needsTransparency) {
    material.transparent = true;
    if (
      material.depthWrite !== undefined &&
      (clampedOpacity < 1 || !!material.alphaMap || material.userData?.editorNeedsTransparency)
    ) {
      material.depthWrite = false;
    }
  } else {
    // Near-opaque: don't keep loader/export BLEND flags (common FBX/OBJ → GLB issue).
    material.transparent = false;
    material.alphaTest = 0;
    if (material.depthWrite !== undefined) material.depthWrite = true;
  }

  // Restore wireframe from glTF material extras (baked on publish — not a glTF field).
  if (
    material.userData?.editorWireframe === true ||
    material.userData?.wireframe === true
  ) {
    material.wireframe = true;
    material.transparent = false;
    material.alphaTest = 0;
    material.opacity = 1;
    if (material.depthWrite !== undefined) material.depthWrite = true;
  }

  // Restore map intensity / scale from extras (lightMapIntensity etc. are not glTF-native).
  applyMapScalars(
    material,
    material.userData?.[EDITOR_MAP_SCALARS_KEY] ||
      material.userData?.mapScalars
  );

  material.needsUpdate = true;
}

/** Apply loaded GLB material state on every child (not only the pivot). */
function applyChildMaterialsAfterGltfLoad(root) {
  if (!root?.traverse) return;

  root.traverse((child) => {
    if (!child.isMesh && !child.isSkinnedMesh) return;

    child.castShadow = true;
    child.receiveShadow = true;

    if (child.userData?.threeMaterialEdited) {
      delete child.userData.threeMaterialEdited;
    }

    const applyOne = (mat) => {
      if (!mat) return;
      syncGltfMaterialSurfaceAfterLoad(mat);
      mat.visible = true;
      if (mat.side === undefined) {
        mat.side = Three.FrontSide;
      }
      preserveMaterial(mat);
    };

    if (!child.material) return;
    if (Array.isArray(child.material)) {
      child.material.forEach(applyOne);
    } else {
      applyOne(child.material);
    }
  });
}

/** Upload path: snapshot original props for the material editor. */
function preserveMaterial(material) {
  if (!material) return;

  fixGltfMaterialsAfterLoad(material);

  if (!material.userData.originalMaterialProps) {
    material.userData.originalMaterialProps = {
      type: material.type,
      color: material.color ? material.color.getHex() : undefined,
      metalness: material.metalness,
      roughness: material.roughness,
      map: material.map || null,
      normalMap: material.normalMap || null,
      roughnessMap: material.roughnessMap || null,
      metalnessMap: material.metalnessMap || null,
      aoMap: material.aoMap || null,
      emissive: material.emissive ? material.emissive.getHex() : undefined,
      emissiveIntensity: material.emissiveIntensity,
      emissiveMap: material.emissiveMap || null,
      opacity: material.opacity,
      transparent: material.transparent,
      alphaTest: material.alphaTest,
      depthWrite: material.depthWrite,
      blending: material.blending,
      side: material.side,
      wireframe: material.wireframe,
    };
  }

  const albedoFromMat = colorToAlbedoHex(material.color);
  const metallicFromMat = {
    metalness:
      typeof material.metalness === 'number' ? material.metalness : 0.5,
    roughness:
      typeof material.roughness === 'number' ? material.roughness : 0.5,
    map: material.userData.material_settings?.metallic?.map ?? '',
  };

  material.userData.material_settings = {
    ...(material.userData.material_settings || {}),
    albedo: albedoFromMat || material.userData.material_settings?.albedo || '#ffffff',
    metallic: metallicFromMat,
    metalicType: !!(
      material.metalnessMap ||
      material.roughnessMap ||
      material.userData.material_settings?.metalicType
    ),
    emissionType: !!(
      material.emissiveMap ||
      material.userData.material_settings?.emissionType
    ),
    syncedFromGlb: true,
    added: true,
  };

  if (material.metalness !== undefined) {
    material.userData.originalMetalness = material.metalness;
  }
  if (material.roughness !== undefined) {
    material.userData.originalRoughness = material.roughness;
  }
}

export function handleMeshUpload(
  e,
  {
    GLTFLoader,
    DRACOLoader,
    scene,
    transformControls,
    selectedObject,
    setPolyCount,
  },
  setPendingUploads
) {
  if (!e.target.files || e.target.files.length === 0) return;

  const file = e.target.files[0];
  const maxSize = 100 * 1024 * 1024;

  if (file.size > maxSize) {
    toast.error('File size should not exceed 100MB.');
    return;
  }

  selectedObject = null;
  store.dispatch(selected_object(null));
  store.dispatch(setLoadingMessage(true));

  const loader = new GLTFLoader();
  registerGltfSkinJointSanitizer(loader);
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
  loader.setDRACOLoader(dracoLoader);

  const objectURL = URL.createObjectURL(file);
  const tempId = `temp-${Date.now()}-${file.name}`;
  const meshAddHistory = beginSuppressedMeshAddHistory(scene);

  // ===============================
  // WORLD-SPACE GEOMETRIC CENTER (FAST)
  // ===============================
  function computeWorldPolyCenter(object) {
    // Use Box3 for fast O(n meshes) calculation instead of slow O(n vertices) iteration
    // This prevents browser hanging on large models
    const box = new Three.Box3().setFromObject(object);
    if (box.isEmpty()) return null;
    const center = new Three.Vector3();
    box.getCenter(center);
    return center;
  }

  loader.load(
    objectURL,
    (gltf) => {
      try {
      const model = gltf.scene;

      // ===============================
      // ANIMATIONS (SAFE)
      // ===============================
      if (Array.isArray(gltf.animations) && gltf.animations.length > 0) {
        const mixer = new Three.AnimationMixer(model);
        model.userData = model.userData || {};
        model.userData.mixer = mixer;
        model.userData.animationActions = [];
        model.userData.animationClips = gltf.animations;

        gltf.animations.forEach((clip) => {
          const action = mixer.clipAction(clip);
          action.reset();
          action.setLoop(Three.LoopRepeat);
          model.userData.animationActions.push(action);
        });
      }

      // ===============================
      // CREATE PIVOT GROUP (KEY FIX)
      // ===============================
      const pivot = new Three.Group();
      pivot.name = tempId;
      pivot._id = tempId;
      pivot.url = tempId;
      pivot.deleteType = 'mesh';
      pivot.objType = '3d';

      scene.add(pivot);

      // ===============================
      // COMPUTE REAL CENTER
      // ===============================
      const worldCenter =
        computeWorldPolyCenter(model) ||
        new Three.Box3().setFromObject(model).getCenter(new Three.Vector3());

      // Move pivot to center
      pivot.position.copy(worldCenter);

      // Move model relative to pivot
      model.position.sub(worldCenter);
      pivot.add(model);

      // ===============================
      // MATERIAL PRESERVATION + SHADOWS
      // ===============================
      model.traverse((child) => {
        if (!child.isMesh) return;

        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(preserveMaterial);
          } else {
            preserveMaterial(child.material);
          }
        }

        child.castShadow = true;
        child.receiveShadow = true;
      });

      // ===============================
      // PENDING UPLOAD QUEUE
      // ===============================
      if (setPendingUploads) {
        setPendingUploads((prev) => [
          ...prev,
          {
            file,
            tempId,
            transform: {
              position_x: 0,
              position_y: 0,
              position_z: 0,
              rotation_x: 0,
              rotation_y: 0,
              rotation_z: 0,
              scale_x: 1,
              scale_y: 1,
              scale_z: 1,
            },
          },
        ]);
      }

      // ===============================
      // METADATA
      // ===============================
      // pivot.objPath = file;
      pivot.file_name = file.name;
      pivot.temp_file_name = tempId;
      pivot.originalFile = file;
      pivot.localFile = file;
      pivot.userData = pivot.userData || {};
      pivot.userData.hasSkinnedMesh = hasSkinnedMeshInHierarchy(model);

      // ===============================
      // SCENE / STATE
      // ===============================
      selectedObject = pivot;

      store.dispatch(setPushIntersectedObject(pivot));
      pivot.position.set(0, 0, 0);
      scene.add(pivot);
      setPolyCount((c) => c + 1);
      store.dispatch(add_new_assets({ type: 'mesh', data: pivot }));
      store.dispatch(set_is_glb_image_editoropen(true));

      selectObjectInScene(tempId);
      toast.success('Mesh added to scene successfully');

      store.dispatch(addModel({ url: objectURL, name: file.name }));
      store.dispatch(addSceneMesh(pivot));
      store.dispatch(setLoadingMessage(false));
      meshAddHistory.finish('add mesh');
      } catch (error) {
        console.error(error);
        toast.error('Failed to add mesh to scene');
        meshAddHistory.cancel();
        store.dispatch(setLoadingMessage(false));
      }
    },
    undefined,
    (error) => {
      console.error(error);
      toast.error('Failed to load mesh file');
      meshAddHistory.cancel();
      store.dispatch(setLoadingMessage(false));
    }
  );
}

function getObjectSizeInCM(object, Three) {
  object.updateWorldMatrix(true, true);

  const box = new Three.Box3().setFromObject(object);

  if (box.isEmpty()) {
    return null;
  }

  const size = new Three.Vector3();
  box.getSize(size);

  return {
    width: size.x * 100, // meters → cm
    height: size.y * 100,
    depth: size.z * 100,
  };
}

export function DownloadMesh(
  meshData,
  { GLTFLoader, DRACOLoader, Three, scene, selectedObject, setPolyCount }
) {
  // ===============================
  // WORLD-SPACE GEOMETRIC CENTER (FAST)
  // ===============================
  function computeWorldPolyCenter(object) {
    // Use Box3 for fast O(n meshes) calculation instead of slow O(n vertices) iteration
    // This prevents browser hanging on large models
    const box = new Three.Box3().setFromObject(object);
    if (box.isEmpty()) return null;
    const center = new Three.Vector3();
    box.getCenter(center);
    return center;
  }

  meshData.forEach((mesh) => {
    if (mesh.glb_type === 'ply') return;
    console.log('Downloading mesh:', mesh);

    const loader = new GLTFLoader();
    registerGltfSkinJointSanitizer(loader);
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    loader.setDRACOLoader(dracoLoader);

    store.dispatch(
      addModel({ url: mesh.asset_file_path, name: mesh.file_name })
    );

    loader.load(
      mesh.asset_file_path,
      (gltf) => {
      (async () => {
      const model = gltf.scene;
      ensureSkinnedGlbReady(model);

      // ===============================
      // CREATE PIVOT GROUP (KEY FIX)
      // ===============================
      const pivot = new Three.Group();
      pivot.name = mesh._id;
      pivot.url = mesh._id;

      const hasPlayAnimationEvent = mesh.events?.some(
        (e) => e.action === 'Play Animation'
      );
      pivot.isAnimationActive =
        mesh.isAnimationActive !== undefined
          ? mesh.isAnimationActive
          : !hasPlayAnimationEvent;

      pivot.deleteType = 'mesh';
      pivot.objType = '3d';

      scene.add(pivot);

      // Auto-center content on the pivot unless the user authored a custom pivot.
      // Re-centering after Edit Pivot Point would snap the mesh onto the pivot.
      const preserveAuthoredPivot =
        isPivotEditedAsset(mesh) || hierarchyHasPivotEditFlag(model);

      if (!preserveAuthoredPivot) {
        const origBox = new Three.Box3().setFromObject(model);
        const worldCenter = origBox.getCenter(new Three.Vector3());
        model.position.sub(worldCenter);
      }
      pivot.add(model);

      if (mesh.glb_type === 'shapes') {
        const childShapeAssets = getChildShapeAssetsFromMeshData(meshData, mesh);
        if (childShapeAssets.length > 0) {
          removeBakedChildShapesFromLoadedModel(
            model,
            mesh.mesh_code || mesh.shape_type,
            childShapeAssets
          );
        }
      }

      // ===============================
      // APPLY TRANSFORMS TO PIVOT
      // ===============================
      pivot.rotation.order = 'XYZ';

      pivot.rotation.set(
        Three.MathUtils.degToRad(mesh.rotation_x),
        Three.MathUtils.degToRad(mesh.rotation_y),
        Three.MathUtils.degToRad(mesh.rotation_z)
      );

      // // Clamp scale: each axis capped at 5 to prevent models from being enormous
      // const clampScale = (v) => Math.min(Math.max(v || 1, 0.001), 5);
      // pivot.scale.set(
      //   clampScale(mesh.scale_x),
      //   clampScale(mesh.scale_y),
      //   clampScale(mesh.scale_z)
      // );
      
      // Use saved scale from API as-is (publish stores pivot scale in scale_x/y/z)
      const scaleFromAsset = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n) || n <= 0) return 1;
        return n;
      };
      pivot.scale.set(
        scaleFromAsset(mesh.scale_x),
        scaleFromAsset(mesh.scale_y),
        scaleFromAsset(mesh.scale_z)
      );

      // position_x/y/z are LOCAL for parented assets; root assets match world position
      pivot.position.set(
        mesh.position_x ?? 0,
        mesh.position_y ?? 0,
        mesh.position_z ?? 0
      );
      pivot.updateMatrixWorld(true);

      // ===============================
      // ANIMATION SETUP (after hierarchy + transforms are final)
      // ===============================
      if (Array.isArray(gltf.animations) && gltf.animations.length > 0) {
        const mixer = new Three.AnimationMixer(model);
        model.userData = model.userData || {};
        model.userData.mixer = mixer;
        model.userData.animationActions = [];
        model.userData.animationClips = gltf.animations;

        gltf.animations.forEach((clip) => {
          const action = mixer.clipAction(clip);
          action.reset();
          action.setLoop(Three.LoopRepeat);
          model.userData.animationActions.push(action);
        });

        ensureSkinnedGlbReady(model);
      }
      const sizeCM = getObjectSizeInCM(model, Three);

      console.log('Final GLB size (cm):', sizeCM);

      /*
      {
        width: 120.3,
        height: 85.7,
        depth: 40.2
      }
      */

      // ===============================
      // MATERIAL / SHADOW SAFETY — per-child GLB materials (opacity, transparent, maps)
      // ===============================
      applyChildMaterialsAfterGltfLoad(model);
      syncMaterialMapBackupsFromSlots(model);
      await restoreMapsFromGltfUserData(model);
      // Restore shared material instances so sibling color edits stay linked.
      relinkMeshesBySharedMaterialId(model);

      // ===============================
      // METADATA RESTORE
      // ===============================
      pivot.events = mesh.events;
      pivot.temp_file_name = mesh.temp_file_name || mesh._id;
      pivot.file_name = mesh.file_name;
      pivot.glb_type = mesh.glb_type || 'normal';
      pivot.mesh_code = mesh.mesh_code || mesh.shape_type || null;
      pivot.isShapes = mesh.glb_type === 'shapes';

      // Restore video texture reference (ID of the Video asset applied as map)
      const videoTextureRef =
        mesh.video_texture ||
        mesh.userData?.video_texture ||
        mesh.material_settings?.video_texture ||
        null;
      if (videoTextureRef) {
        pivot.video_texture = videoTextureRef;
      }

      // Detect library/sketchfab identity BEFORE dispatching to Redux,
      // so pivot.userData is fully built before Immer freezes it.
      const registryIdentity = getLibraryIdentity(mesh._id);
      const assetFilePath = mesh.asset_file_path || '';
      const isSketchfabUrl = assetFilePath.includes('sketchfab');
      const isTempLibrary = mesh.temp_file_name && String(mesh.temp_file_name).startsWith('library-');
      const isFromLibraryByFlag = mesh.from_library || mesh.source === 'library' || registryIdentity?.source === 'library';
      const isFromSketchfabByFlag = mesh.source === 'sketchfab' || registryIdentity?.source === 'sketchfab';

      // Restore source identity on the pivot object itself
      if (isFromLibraryByFlag || isTempLibrary) {
        pivot.source = 'library';
        pivot.from_library = true;
        pivot.libraryAssetId = mesh.libraryAssetId || registryIdentity?.libraryAssetId || pivot.libraryAssetId;
      } else if (isFromSketchfabByFlag || isSketchfabUrl) {
        pivot.source = 'sketchfab';
      }

      if (preserveAuthoredPivot) {
        pivot.pivotEdited = true;
        model.userData = model.userData || {};
        model.userData.pivotEdited = true;
      }

      // Determine material_settings to store on userData
      let pivotMaterialSettings = null;
      if (mesh.material_settings && hasMaterialModifications(mesh.material_settings)) {
        pivotMaterialSettings = mesh.material_settings;
      } else if (isFromLibraryByFlag || isTempLibrary || isFromSketchfabByFlag || isSketchfabUrl) {
        pivotMaterialSettings = mesh.material_settings || {};
      }

      // Build the full userData object ONCE before Redux dispatches — prevents
      // Immer from freezing individual properties that we later need to write.
      pivot.userData = {
        ...(pivot.userData || {}),
        glb_type: pivot.glb_type,
        mesh_code: pivot.mesh_code,
        isShapes: pivot.isShapes,
        hasSkinnedMesh: hasSkinnedMeshInHierarchy(model),
        // Library/sketchfab identity
        ...(isFromLibraryByFlag || isTempLibrary
          ? { source: 'library', from_library: true }
          : isFromSketchfabByFlag || isSketchfabUrl
            ? { source: 'sketchfab' }
            : {}),
        // Pivot edit persistence
        ...(preserveAuthoredPivot
          ? { pivotEdited: true, pivot_edited: true }
          : {}),
        // Material settings (may be overwritten after MATERIAL RESTORE below)
        ...(pivotMaterialSettings !== null ? { material_settings: pivotMaterialSettings } : {}),
        ...((() => {
          const restoredRenderView =
            mesh.renderView ||
            mesh.material_settings?.renderView ||
            mesh.rendererType ||
            mesh.material_settings?.rendererType ||
            null;
          return restoredRenderView
            ? {
                renderView: restoredRenderView,
                material_settings: {
                  ...(pivotMaterialSettings || mesh.material_settings || {}),
                  renderView: restoredRenderView,
                },
              }
            : {};
        })()),
        ...(videoTextureRef ? { video_texture: videoTextureRef } : {}),
      };

      const restoredRenderView =
        mesh.renderView ||
        mesh.material_settings?.renderView ||
        mesh.rendererType ||
        mesh.material_settings?.rendererType ||
        null;
      if (restoredRenderView) {
        pivot.renderView = restoredRenderView;
        // Viewport only — list materials still reflect reconstructed GLB data.
        applyRenderView(pivot, restoredRenderView);
      }

      // Cache GLB bytes for safe republish of rigged models (transform/materials live in JSON).
      const glbUrl = mesh.asset_file_path;
      if (glbUrl && typeof fetch === 'function') {
        fetch(glbUrl)
          .then((res) => (res.ok ? res.arrayBuffer() : null))
          .then((buffer) => {
            if (!buffer?.byteLength) return;
            const fileName = mesh.file_name || 'model.glb';
            const cached = new File([buffer], fileName, {
              type: 'model/gltf-binary',
            });
            pivot.originalFile = cached;
            pivot.localFile = cached;
          })
          .catch(() => {});
      }

      // ===============================
      // SCENE / STATE REGISTRATION
      // ===============================
      selectedObject = pivot;
      setPolyCount((c) => c + 1);

      store.dispatch(setPushIntersectedObject(pivot));
      store.dispatch(selected_object(pivot));
      store.dispatch(addSceneMesh(pivot));
      selectObjectInScene(mesh._id);

      // Notify AppLeftSection that a GLB pivot is now live in the scene so it
      // can re-run child extraction (scene.children mutations are not detectable
      // by React, so we use a custom event as the reliable signal).
      try {
        window.dispatchEvent(
          new CustomEvent('glbMeshLoaded', {
            detail: { meshId: mesh._id, pivotName: pivot.name },
          })
        );
      } catch (_) {}

      // ===============================
      // MATERIAL RESTORE (API metadata + per-child overrides for skinned GLBs)
      // ===============================
      if (mesh.material_settings && typeof mesh.material_settings === 'object') {
        const glbType = mesh.glb_type || 'normal';
        const legacyMaterialOverrides = hasMaterialModifications(
          mesh.material_settings
        );
        const needsUnlitEmissionTexture =
          legacyMaterialOverrides &&
          mesh.material_settings?.shader === 'Unlit' &&
          mesh.material_settings?.textureImage;

        // Replace userData (not mutate) since Immer may have frozen it via addSceneMesh.
        pivot.userData = { ...pivot.userData, material_settings: mesh.material_settings };

        if (needsUnlitEmissionTexture) {
          model.traverse((child) => {
            if (
              child.isMesh &&
              child.material?.isMeshBasicMaterial &&
              mesh.material_settings.textureImage
            ) {
              new Three.TextureLoader().load(
                mesh.material_settings.textureImage,
                (tex) => {
                  child.material.map = tex;
                  child.material.needsUpdate = true;
                }
              );
            }
          });
        } else if (
          (legacyMaterialOverrides ||
            glbType === 'shapes' ||
            hasStoredSurfaceMaterialEdits(mesh.material_settings) ||
            mesh.from_library ||
            mesh.source === 'library') &&
          glbType !== ''
        ) {
          const isLibraryOrSketchfab = pivot.source === 'library' || pivot.source === 'sketchfab';

          if (isLibraryOrSketchfab) {
            // For library/sketchfab assets: skip cloneMaterialSettingsForDuplicate
            // which reads live mesh colors and overwrites childOverrides with stale defaults.
            // Apply the API material_settings directly so the saved albedo is preserved.
            const apiSettings = { ...mesh.material_settings, __lastChangedKey: 'restore' };
            UpdateMaterial({ object: pivot, addData: apiSettings });

            // Library publish skips GLB re-bake — restore map textures from
            // material_settings.maps / mapPreviewUrls when slots are empty.
            await restoreMaterialMapsFromSettings(model, mesh.material_settings);
            syncMaterialMapBackupsFromSlots(model);
            applyMapScalarsFromSettings(model, mesh.material_settings);

            // Apply saved childOverrides, or fall back to painting albedo on all children
            const savedOverrides = resolveChildMaterialOverrides(mesh.material_settings);
            if (savedOverrides) {
              applyChildMaterialOverrides(pivot, savedOverrides);
            } else if (mesh.material_settings?.albedo && mesh.material_settings.albedo !== '#ffffff' && mesh.material_settings.albedo !== '#00FFFFFF') {
              pivot.traverse((child) => {
                if (child.isMesh && child.material) {
                  const mats = Array.isArray(child.material) ? child.material : [child.material];
                  mats.forEach((mat) => {
                    if (mat.color) {
                      mat.color.set(mesh.material_settings.albedo);
                      mat.needsUpdate = true;
                    }
                  });
                }
              });
            }
            // Refresh userData after UpdateMaterial may have re-merged settings
            pivot.userData = { ...pivot.userData, material_settings: pivot.userData?.material_settings || mesh.material_settings };
          } else {
            await applyMaterialSettingsAfterLoad(
              pivot,
              model,
              mesh.material_settings
            );
          }
        } else if (mesh.material_settings?.mapVisibility) {
          Object.entries(mesh.material_settings.mapVisibility).forEach(
            ([mapKey, visible]) => {
              if (typeof visible !== 'boolean') return;
              setMaterialMapVisibility(model, mapKey, visible);
            }
          );
        }

        // Always last: per-child colors/wireframe for animation GLBs (original
        // skin binary is republished; overrides live in material_settings JSON).
        // Also re-applied when bake succeeded — idempotent with baked colors.
        const childOverrides = resolveChildMaterialOverrides(
          mesh.material_settings
        );
        if (childOverrides) {
          applyChildMaterialOverrides(pivot, childOverrides);
        } else if (mesh.material_settings?.wireframe_mesh_indices) {
          applyWireframeMeshIndices(
            pivot,
            mesh.material_settings.wireframe_mesh_indices
          );
        }
        // Indices can also reinforce wireframe when overrides lacked the flag.
        if (
          childOverrides &&
          mesh.material_settings?.wireframe_mesh_indices
        ) {
          applyWireframeMeshIndices(
            pivot,
            mesh.material_settings.wireframe_mesh_indices
          );
        }

        // Ensure map intensity / scale survive reconstruct (settings + baked extras).
        applyMapScalarsFromSettings(pivot, mesh.material_settings);
        applyMapScalarsFromSettings(model, mesh.material_settings);
      }
      })().catch((err) => {
        console.error('[DownloadMesh] post-load material restore failed:', err);
      });
    },
      undefined,
      (err) => {
        console.error('[DownloadMesh] GLB load failed:', mesh?._id, err);
        toast.error(
          `Failed to load mesh${mesh?.file_name ? ` "${mesh.file_name}"` : ''}. The file may be invalid or corrupted.`
        );
      }
    );
  });
}

// ──────────────────────────────────────────────────────────────
// Smart incremental copy naming – fixed version
// ──────────────────────────────────────────────────────────────


function setMeshAssetParentInRedux(childId, parentId) {
  if (!childId || !parentId) return;
  const meshes = store.getState().assets_object?.assets_data?.mesh || [];
  let found = false;
  const updated = meshes.map((entry) => {
    const entryId = entry._id || entry.id || entry.name || entry.url;
    if (entryId !== childId) return entry;
    found = true;
    if (entry?.isObject3D) {
      entry.userData = entry.userData || {};
      entry.userData.parentAssetId = parentId;
      return entry;
    }
    return { ...entry, parent: parentId };
  });
  if (!found) {
    updated.push({
      _id: childId,
      id: childId,
      name: childId,
      parent: parentId,
    });
  }
  store.dispatch(update_assets_data({ type: 'mesh', data: updated }));
}

function captureChildLocalTransform(child, parent, Three) {
  if (!child) return null;
  if (parent && child.parent === parent) {
    return {
      position: child.position.clone(),
      rotation: child.rotation.clone(),
      scale: child.scale.clone(),
    };
  }
  if (!parent) {
    return {
      position: child.position.clone(),
      rotation: child.rotation.clone(),
      scale: child.scale.clone(),
    };
  }

  parent.updateMatrixWorld(true);
  child.updateMatrixWorld(true);

  const localPos = new Three.Vector3();
  parent.worldToLocal(
    localPos.copy(child.getWorldPosition(new Three.Vector3()))
  );

  const parentQuat = new Three.Quaternion();
  const childQuat = new Three.Quaternion();
  parent.getWorldQuaternion(parentQuat);
  child.getWorldQuaternion(childQuat);
  const localQuat = parentQuat.clone().invert().multiply(childQuat);
  const localRot = new Three.Euler().setFromQuaternion(
    localQuat,
    child.rotation.order
  );

  const childWorldScale = new Three.Vector3();
  const parentWorldScale = new Three.Vector3();
  child.getWorldScale(childWorldScale);
  parent.getWorldScale(parentWorldScale);

  return {
    position: localPos,
    rotation: localRot,
    scale: new Three.Vector3(
      childWorldScale.x / (parentWorldScale.x || 1),
      childWorldScale.y / (parentWorldScale.y || 1),
      childWorldScale.z / (parentWorldScale.z || 1)
    ),
  };
}

/** Add duplicate under parent using local transform (not attach() world preservation). */
function applyDuplicateUnderParent(parent, child, localTransform) {
  if (!parent || !child) return;
  parent.add(child);
  if (localTransform) {
    child.position.copy(localTransform.position);
    child.rotation.copy(localTransform.rotation);
    child.scale.copy(localTransform.scale);
  }
  child.updateMatrixWorld(true);
}

function duplicateNestedMeshChildren(
  parentDuplicate,
  nestedChildren,
  objectt,
  setPendingUploads,
  originalParent,
  localTransformMap = null
) {
  if (!parentDuplicate || !nestedChildren?.length) return;

  nestedChildren.forEach((childObj) => {
    const localTransform =
      localTransformMap?.get(childObj.uuid) ||
      captureChildLocalTransform(
        childObj,
        originalParent,
        objectt.Three
      );

    const childItem =
      findMeshItemForSceneObject(childObj) || {
        _id: childObj._id,
        id: childObj._id,
        name: childObj.name,
        url: childObj.url,
        file_name: childObj.file_name,
        mesh_code: childObj.mesh_code || childObj.userData?.mesh_code,
        glb_type: childObj.glb_type || childObj.userData?.glb_type,
        material_settings: childObj.userData?.material_settings,
        asset_file_path: childObj.asset_file_path,
        asset_file_pathh: childObj.asset_file_pathh,
      };

    duplicateMeshAsset(
      childItem,
      objectt,
      setPendingUploads,
      childObj,
      { attachToParent: parentDuplicate, localTransform }
    );
  });
}

/** Resolve the live Three.js object for an asset item (rename-safe). */
export function resolveMeshInScene(scene, item, fallbackMesh = null) {
  if (fallbackMesh && typeof fallbackMesh.clone === 'function') {
    return fallbackMesh;
  }
  if (!scene || !item) return fallbackMesh || null;
  if (typeof item?.clone === 'function') return item;

  const candidateIds = [
    item?.temp_file_name,
    item?.text_material?.id,
    item?.material_settings?.id,
    item?.userData?.text_material?.id,
    item?.userData?.material_settings?.id,
    item?._id,
    item?.url,
    item?.name,
    item?.id,
  ].filter(Boolean);

  for (const id of candidateIds) {
    const obj = scene.getObjectByName(id);
    if (obj) return obj;
  }

  if (candidateIds.length > 0) {
    let found = null;
    scene.traverse((obj) => {
      if (found) return;
      if (
        candidateIds.includes(obj.name) ||
        candidateIds.includes(obj.url) ||
        candidateIds.includes(obj._id)
      ) {
        found = obj;
      }
    });
    if (found) return found;
  }

  return item?.object || fallbackMesh || null;
}

export function duplicateMeshAsset(
  item,
  {
    Three,
    scene,
    transformControls,
    selectedObject,
    GLTFLoader,
    DRACOLoader,
    SplatLoader,
    setPolyCount,
  },
  setPendingUploads,
  originalMesh = null,
  options = {}
) {
  const attachToParent = options.attachToParent || null;
  const localTransform = options.localTransform || null;
  if (!item) {
    toast.error(
      'Valid item with file path and name are required for duplication'
    );
    return;
  }

  // When item has localFile but no asset_file_path/file_name, use handleMeshUpload logic
  if (item.localFile && (!item.asset_file_path || !item.file_name)) {
    const file = item.localFile;
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size should not exceed 50MB.');
      return;
    }
    selectedObject = null;
    store.dispatch(selected_object(null));
    transformControls?.detach();
    store.dispatch(setLoadingMessage(true));

    const loader = new GLTFLoader();
    registerGltfSkinJointSanitizer(loader);
    if (DRACOLoader) {
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
      loader.setDRACOLoader(dracoLoader);
    }

    const objectURL = URL.createObjectURL(file);
    // Use item.file_name if available (handles renamed GLBs), otherwise fall back to file.name
    const nameToUse = item.file_name || item.asset_file_name || file.name;
    const baseName = nameToUse.replace(/\.[^/.]+$/, '');
    // Get extension from item.file_name first, then fall back to file extension
    const ext =
      (item.file_name && item.file_name.match(/\.[^/.]+$/)?.[0]) ||
      nameToUse.match(/\.[^/.]+$/)?.[0] ||
      file.name.match(/\.[^/.]+$/)?.[0] ||
      '';
    const duplicateFileName = getNextCopyName(scene, baseName) + ext;
    const tempId = buildTempFileName(duplicateFileName);

    function computeWorldPolyCenter(object) {
      // Use Box3 for fast O(n meshes) calculation instead of slow O(n vertices) iteration
      // This prevents browser hanging on large models
      const box = new Three.Box3().setFromObject(object);
      if (box.isEmpty()) return null;
      const center = new Three.Vector3();
      box.getCenter(center);
      return center;
    }

    loader.load(
      objectURL,
      (gltf) => {
        const model = gltf.scene;

        if (Array.isArray(gltf.animations) && gltf.animations.length > 0) {
          const mixer = new Three.AnimationMixer(model);
          model.userData = model.userData || {};
          model.userData.mixer = mixer;
          model.userData.animationActions = [];
          model.userData.animationClips = gltf.animations;
          gltf.animations.forEach((clip) => {
            const action = mixer.clipAction(clip);
            action.reset();
            action.setLoop(Three.LoopRepeat);
            model.userData.animationActions.push(action);
          });
        }

        const pivot = new Three.Group();
        pivot.name = tempId;
        pivot._id = tempId;
        pivot.url = tempId;
        pivot.deleteType = 'mesh';
        pivot.objType = '3d';
        scene.add(pivot);

        const worldCenter =
          computeWorldPolyCenter(model) ||
          new Three.Box3().setFromObject(model).getCenter(new Three.Vector3());
        pivot.position.copy(worldCenter);
        model.position.sub(worldCenter);
        pivot.add(model);

        model.traverse((child) => {
          if (!child.isMesh && !child.isSkinnedMesh) return;
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(preserveMaterial);
            } else {
              preserveMaterial(child.material);
            }
          }
          child.castShadow = true;
          child.receiveShadow = true;
        });

        // Prioritize scene object (tempMesh or originalMesh) for current transform
        const sourceMesh =
          originalMesh ||
          scene.getObjectByName(item?._id || item?.id || item?.url || item?.name);
        const sourcePivot = resolveGlbPivotFromScene(scene, item, sourceMesh);
        const mergedItem = sourcePivot
          ? mergeDuplicateSourceItem(sourcePivot, item)
          : item;

        if (sourcePivot) {
          applyDuplicateGlbMaterials({
            gltfModel: model,
            pivot,
            sourcePivot,
            item: mergedItem,
            Three,
          });
        }

        pivot.position.set(
          (sourceMesh?.position?.x || 0) + 0.5,
          sourceMesh?.position?.y || 0,
          sourceMesh?.position?.z || 0
        );
        pivot.rotation.copy(sourceMesh?.rotation || new Three.Euler());
        pivot.scale.copy(sourceMesh?.scale || new Three.Vector3(1, 1, 1));

        pivot.objPath = file;
        pivot.file_name = duplicateFileName;
        pivot.temp_file_name = tempId;
        pivot.originalFile = file;
        pivot.localFile = file;
        pivot.glb_type =
          sourcePivot?.glb_type || mergedItem?.glb_type || item?.glb_type;
        if (sourcePivot?.isAnimationActive !== undefined) {
          pivot.isAnimationActive = sourcePivot.isAnimationActive;
        }

        if (sourcePivot) {
          const duplicateMeshCode = `mesh_${Math.random()
            .toString(36)
            .substr(2, 6)}`;
          pivot.userData = {
            ...(sourcePivot.userData || {}),
            file_name: duplicateFileName,
            position_x: pivot.position.x,
            position_y: pivot.position.y,
            position_z: pivot.position.z,
            scale_x: pivot.scale.x,
            scale_y: pivot.scale.y,
            scale_z: pivot.scale.z,
            rotation_x: sourcePivot.userData?.rotation_x ?? 0,
            rotation_y: sourcePivot.userData?.rotation_y ?? 0,
            rotation_z: sourcePivot.userData?.rotation_z ?? 0,
            mesh_code: duplicateMeshCode,
            saved: false,
            isDuplicate: true,
            isGlbPlaying: false,
            activePlayModelEffect: null,
            duplicateSourceFile: file,
            material_settings: cloneMaterialSettingsForDuplicate(
              sourcePivot,
              sourcePivot.userData?.material_settings ||
                mergedItem?.material_settings
            ),
          };
          // Drop circular animation runtime refs copied from source userData.
          NON_CLONEABLE_USERDATA_KEYS.forEach((key) => {
            delete pivot.userData[key];
          });
          if (sourcePivot.userData?.threeMaterialEdited) {
            pivot.userData.threeMaterialEdited = true;
          }
          applyDuplicateMaterialToObject(pivot, sourcePivot);
          isolateDuplicatePublishIdentity(pivot);
        } else {
          pivot.userData = pivot.userData || {};
          pivot.userData.duplicateSourceFile = file;
          isolateDuplicatePublishIdentity(pivot);
        }

        if (setPendingUploads) {
          setPendingUploads((prev) => [
            ...prev,
            {
              file,
              tempId,
              transform: {
                position_x: (sourceMesh?.position?.x || 0) + 0.5,
                position_y: sourceMesh?.position?.y || 0,
                position_z: sourceMesh?.position?.z || 0,
                rotation_x: Three.MathUtils.radToDeg(sourceMesh?.rotation?.x || 0),
                rotation_y: Three.MathUtils.radToDeg(sourceMesh?.rotation?.y || 0),
                rotation_z: Three.MathUtils.radToDeg(sourceMesh?.rotation?.z || 0),
                scale_x: sourceMesh?.scale?.x || 1,
                scale_y: sourceMesh?.scale?.y || 1,
                scale_z: sourceMesh?.scale?.z || 1,
              },
            },
          ]);
        }

        store.dispatch(setPushIntersectedObject(pivot));
        if (setPolyCount) setPolyCount((c) => c + 1);
        store.dispatch(add_new_assets({ type: 'mesh', data: pivot }));
        store.dispatch(set_is_glb_image_editoropen(true));
        selectObjectInScene(tempId);
        toast.success('Mesh added to scene successfully');
        store.dispatch(addModel({ url: objectURL, name: duplicateFileName }));
        store.dispatch(addSceneMesh(pivot));
        isolateGlbAnimationMixers(pivot);
        stopPlayModelForObject(pivot);
        store.dispatch(setLoadingMessage(false));
      },
      undefined,
      (error) => {
        console.error(error);
        toast.error('Failed to load mesh file');
        store.dispatch(setLoadingMessage(false));
      }
    );
    return;
  }

  // Before publish: no asset_file_path/file_name and no localFile — duplicate by cloning the mesh in the scene
  let meshInScene = resolveMeshInScene(scene, item, originalMesh);
  if (meshInScene) {
    item = mergeDuplicateSourceItem(meshInScene, item);
  }

  const isPlyBeforeClone =
    item?.glb_type === 'ply' ||
    meshInScene?.glb_type === 'ply' ||
    (item?.file_name && /\.ply$/i.test(item.file_name)) ||
    (meshInScene?.file_name && /\.ply$/i.test(meshInScene.file_name));

  if (isPlyBeforeClone && meshInScene) {
    selectedObject = null;
    store.dispatch(selected_object(null));
    transformControls?.detach();
    store.dispatch(setLoadingMessage(true));
    return duplicateMeshPlyAsset(
      item,
      { Three, scene, transformControls, selectedObject, SplatLoader },
      setPendingUploads,
      meshInScene
    );
  }

  if (
    meshInScene &&
    typeof meshInScene.clone === 'function' &&
    (!item.asset_file_path || !item.file_name)
  ) {
    // Rigged GLBs cannot be deep-cloned safely — reload from file instead.
    if (hasSkinnedMeshInHierarchy(meshInScene)) {
      const sourcePivot = resolveGlbPivotFromScene(scene, item, meshInScene);
      const glbFile = resolveDuplicateSourceFile(
        sourcePivot,
        item,
        meshInScene
      );
      if (glbFile) {
        return duplicateMeshAsset(
          {
            ...item,
            localFile: glbFile,
            originalFile: glbFile,
            file_name:
              item?.file_name ||
              sourcePivot?.file_name ||
              meshInScene.file_name ||
              glbFile.name,
          },
          {
            Three,
            scene,
            transformControls,
            selectedObject,
            GLTFLoader,
            DRACOLoader,
            SplatLoader,
            setPolyCount,
          },
          setPendingUploads,
          sourcePivot || meshInScene
        );
      }
    }

    selectedObject = null;
    store.dispatch(selected_object(null));
    transformControls?.detach();

    // Prefer the display name from the asset item (handles renamed shapes & GLB child meshes),
    // then fall back to the scene object's own naming.
    // IMPORTANT: Check scene object properties BEFORE item?.name because item?.name is not updated during rename
    const baseName = (
      item?.file_name ||
      meshInScene.file_name ||
      meshInScene.userData?.file_name ||
      item?.name ||
      meshInScene.name ||
      'mesh'
    ).replace(/\.[^/.]+$/, '');

    const ext =
      (item?.file_name && item.file_name.match(/\.[^/.]+$/)?.[0]) ||
      meshInScene.file_name?.match(/\.[^/.]+$/)?.[0] ||
      meshInScene.name?.match(/\.[^/.]+$/)?.[0] ||
      '';
    const duplicateFileName = getNextCopyName(scene, baseName) + ext;
    const tempId = buildTempFileName(duplicateFileName);
    const duplicateMeshCode = `mesh_${Math.random().toString(36).substr(2, 6)}`;

    const nestedChildren = getNestedSceneMeshChildren(meshInScene, scene);
    syncNestedChildrenSceneParent(meshInScene, nestedChildren);
    const childLocalTransformMap = new Map(
      nestedChildren.map((child) => [
        child.uuid,
        captureChildLocalTransform(child, meshInScene, Three),
      ])
    );
    const { detached: detachedForClone, savedLocals: detachedLocalsForClone } =
      detachNestedShapeAssetsForExport(meshInScene, scene);
    const userDataRuntimeBackup = stripNonCloneableUserDataRuntime(meshInScene);
    let duplicatedMesh;
    try {
      duplicatedMesh = meshInScene.clone(true);
    } finally {
      restoreNonCloneableUserDataRuntime(userDataRuntimeBackup);
      reattachDetachedShapeAssets(
        meshInScene,
        detachedForClone,
        detachedLocalsForClone
      );
    }
    assignFreshObjectUuids(duplicatedMesh, Three);

    duplicatedMesh.traverse((child) => {
      if (child.geometry) child.geometry = child.geometry.clone();
      if (child.material) {
        const mat = child.material;
        if (Array.isArray(mat)) {
          child.material = mat.map(cloneMaterialWithEditorState);
        } else {
          child.material = cloneMaterialWithEditorState(mat);
        }
      }
    });

    // Animation clips were stripped before clone — copy them from the live source.
    copyAnimationDataFromSource(meshInScene, duplicatedMesh);

    const sourceInner = resolveGltfExportRoot(meshInScene);
    const targetInner = resolveGltfExportRoot(duplicatedMesh);
    if (sourceInner && targetInner) {
      copyGlbMaterialsFromSource(sourceInner, targetInner);
    }

    duplicatedMesh.name = tempId;
    duplicatedMesh.url = tempId;
    duplicatedMesh._id = tempId;
    duplicatedMesh.temp_file_name = tempId;
    duplicatedMesh.deleteType = 'mesh';
    duplicatedMesh.objType = meshInScene.objType || '3d';
    // Preserve the original .glb URL so GLB child detection works in AppLeftSection
    // before the asset is published (asset_file_path is set by the server on publish).
    // We read it from meshInScene because duplicatedMesh.url was just overwritten above.
    const _sourceGlbUrl =
      meshInScene.asset_file_path ||
      item.asset_file_path ||
      meshInScene.url ||   // library pivot stores S3 .glb URL here before clone
      item.url ||
      null;
    if (_sourceGlbUrl && typeof _sourceGlbUrl === 'string' && _sourceGlbUrl.toLowerCase().includes('.glb')) {
      duplicatedMesh.asset_file_path = _sourceGlbUrl;
    }
    duplicatedMesh.file_name = duplicateFileName;
    if (attachToParent) {
      // Local transform applied in applyDuplicateUnderParent — avoid world-space attach().
    } else {
      duplicatedMesh.position.set(
        (meshInScene.position.x || 0) + 0.5,
        meshInScene.position.y || 0,
        meshInScene.position.z || 0
      );
    }
    if (!attachToParent) {
      duplicatedMesh.rotation.copy(meshInScene.rotation);
      duplicatedMesh.scale.copy(meshInScene.scale);
    }

    const isShapeDuplicate = applyShapeMetadataToDuplicate(
      duplicatedMesh,
      meshInScene,
      item,
      duplicateFileName
    );
    if (!isShapeDuplicate) {
      duplicatedMesh.userData = {
        ...(meshInScene.userData || {}),
        file_name: duplicateFileName,
        position_x: duplicatedMesh.position.x,
        position_y: duplicatedMesh.position.y,
        position_z: duplicatedMesh.position.z,
        scale_x: duplicatedMesh.scale.x,
        scale_y: duplicatedMesh.scale.y,
        scale_z: duplicatedMesh.scale.z,
        rotation_x: meshInScene.userData?.rotation_x ?? 0,
        rotation_y: meshInScene.userData?.rotation_y ?? 0,
        rotation_z: meshInScene.userData?.rotation_z ?? 0,
        mesh_code: duplicateMeshCode,
        saved: false,
        isDuplicate: true,
        isGlbPlaying: false,
        activePlayModelEffect: null,
      };
      if (meshInScene.userData?.material_settings) {
        duplicatedMesh.userData.material_settings =
          cloneMaterialSettingsForDuplicate(
            meshInScene,
            meshInScene.userData.material_settings
          );
      }
      // Keep live per-child overrides (wireframe/color) on the duplicate root.
      if (meshInScene.userData?.material_settings?.childOverrides) {
        try {
          duplicatedMesh.userData.material_settings = {
            ...(duplicatedMesh.userData.material_settings || {}),
            childOverrides: JSON.parse(
              JSON.stringify(
                meshInScene.userData.material_settings.childOverrides
              )
            ),
          };
        } catch {
          duplicatedMesh.userData.material_settings = {
            ...(duplicatedMesh.userData.material_settings || {}),
            childOverrides:
              meshInScene.userData.material_settings.childOverrides,
          };
        }
      }
      if (meshInScene.userData?.threeMaterialEdited) {
        duplicatedMesh.userData.threeMaterialEdited = true;
      }
      duplicatedMesh.glb_type = meshInScene.glb_type || item.glb_type;
    } else {
      duplicatedMesh.userData = {
        ...duplicatedMesh.userData,
        position_x: duplicatedMesh.position.x,
        position_y: duplicatedMesh.position.y,
        position_z: duplicatedMesh.position.z,
        scale_x: duplicatedMesh.scale.x,
        scale_y: duplicatedMesh.scale.y,
        scale_z: duplicatedMesh.scale.z,
        rotation_x: meshInScene.userData?.rotation_x ?? 0,
        rotation_y: meshInScene.userData?.rotation_y ?? 0,
        rotation_z: meshInScene.userData?.rotation_z ?? 0,
        saved: false,
        isDuplicate: true,
        isGlbPlaying: false,
        activePlayModelEffect: null,
      };
    }

    const sourceFile = resolveDuplicateSourceFile(
      meshInScene,
      item,
      meshInScene
    );
    if (sourceFile) {
      duplicatedMesh.localFile = sourceFile;
      duplicatedMesh.originalFile = sourceFile;
      duplicatedMesh.userData = duplicatedMesh.userData || {};
      duplicatedMesh.userData.duplicateSourceFile = sourceFile;
    }

    isolateGlbAnimationMixers(duplicatedMesh);
    stopPlayModelForObject(duplicatedMesh);
    isolateDuplicatePublishIdentity(duplicatedMesh);
    applyDuplicateMaterialToObject(duplicatedMesh, meshInScene);
    attachClonedEventsToDuplicate(duplicatedMesh, {
      sourceItem: item,
      sourceObject: meshInScene,
      events: item.events || meshInScene.events || meshInScene.userData?.events,
    });
    if (attachToParent) {
      applyDuplicateUnderParent(
        attachToParent,
        duplicatedMesh,
        localTransform ||
          captureChildLocalTransform(meshInScene, meshInScene?.parent, Three)
      );
    } else {
      scene.add(duplicatedMesh);
    }
    store.dispatch(setPushIntersectedObject(duplicatedMesh));
    store.dispatch(addSceneMesh(duplicatedMesh));
    store.dispatch(add_new_assets({ type: 'mesh', data: duplicatedMesh }));

    // Fire glbMeshLoaded so AssetList re-runs GLB child extraction immediately
    // for the duplicate (same event used by LibraryAsset.js on original import).
    try {
      window.dispatchEvent(
        new CustomEvent('glbMeshLoaded', {
          detail: { meshId: tempId, pivotName: duplicatedMesh.name },
        })
      );
    } catch (_) {}

    if (attachToParent) {
      const parentId =
        attachToParent._id || attachToParent.name || attachToParent.url;
      setMeshAssetParentInRedux(tempId, parentId);
    }
    store.dispatch(selected_object(duplicatedMesh));

    if (!attachToParent && nestedChildren.length > 0) {
      duplicateNestedMeshChildren(
        duplicatedMesh,
        nestedChildren,
        {
          Three,
          scene,
          transformControls,
          selectedObject,
          GLTFLoader,
          DRACOLoader,
          SplatLoader,
          setPolyCount,
        },
        setPendingUploads,
        meshInScene,
        childLocalTransformMap
      );
    }

    const cloneDisplayName =
      duplicatedMesh.name?.length > 30
        ? `${duplicatedMesh.name.substring(0, 30)}...`
        : duplicatedMesh.name;
    toast.success(`Element duplicated as "${cloneDisplayName}"`);
    return;
  }

  if (!item.asset_file_path || !item.file_name) {
    toast.error(
      'Valid item with file path and name are required for duplication'
    );
    return;
  }

  // Clear current selection
  selectedObject = null;
  store.dispatch(selected_object(null));
  transformControls?.detach();

  // Set loading state
  store.dispatch(setLoadingMessage(true));

  // Generate unique properties for duplicate
  const baseName = item.file_name.replace(/\.[^/.]+$/, ''); // strip extension
  const duplicateFileName =
    getNextCopyName(scene, baseName) +
    (item.file_name.match(/\.[^/.]+$/)?.[0] || '');
  const tempId = buildTempFileName(duplicateFileName);
  const duplicateMeshCode = `mesh_${Math.random().toString(36).substr(2, 6)}`;

  // If this asset is actually a PLY/splat (some items have glb_type='ply' or file path ends with .ply),
  // delegate to the PLY duplication routine which uses SplatLoader.
  const isPly =
    (item.glb_type && item.glb_type === 'ply') ||
    (item.asset_file_path &&
      item.asset_file_path.toLowerCase().endsWith('.ply'));
  if (isPly) {
    try {
      return duplicateMeshPlyAsset(
        item,
        { Three, scene, transformControls, selectedObject, SplatLoader },
        setPendingUploads,
        originalMesh
      );
    } catch (err) {
      console.error(
        'Failed to duplicate as PLY via duplicateMeshPlyAsset:',
        err
      );
      store.dispatch(setLoadingMessage(false));
      toast.error('Failed to duplicate mesh (PLY)');
      return;
    }
  }

  const isObj =
    (item.glb_type && item.glb_type === 'obj') ||
    (item.asset_file_path &&
      item.asset_file_path.toLowerCase().endsWith('.obj'));
  if (isObj) {
    try {
      return duplicateMeshObjAsset(
        item,
        { Three, scene, transformControls, selectedObject, OBJLoader },
        setPendingUploads,
        originalMesh
      );
    } catch (err) {
      console.error(
        'Failed to duplicate as OBJ via duplicateMeshObjAsset:',
        err
      );
      store.dispatch(setLoadingMessage(false));
      toast.error('Failed to duplicate mesh (OBJ)');
      return;
    }
  }

  const isFbx =
    (item.glb_type && item.glb_type === 'fbx') ||
    (item.asset_file_path &&
      item.asset_file_path.toLowerCase().endsWith('.fbx'));
  if (isFbx) {
    try {
      return duplicateMeshFbxAsset(
        item,
        { Three, scene, transformControls, selectedObject, FBXLoader },
        setPendingUploads,
        originalMesh
      );
    } catch (err) {
      console.error(
        'Failed to duplicate as FBX via duplicateMeshFbxAsset:',
        err
      );
      store.dispatch(setLoadingMessage(false));
      toast.error('Failed to duplicate mesh (FBX)');
      return;
    }
  }

  const sourcePivot = resolveGlbPivotFromScene(scene, item, originalMesh);
  if (sourcePivot) {
    item = mergeDuplicateSourceItem(sourcePivot, item);
  }
  const loadSource = resolveDuplicateGlbLoadUrl(item, sourcePivot);
  if (!loadSource?.url) {
    toast.error(
      'Valid item with file path and name are required for duplication'
    );
    store.dispatch(setLoadingMessage(false));
    return;
  }

  const duplicateGltfLoader = new GLTFLoader();
  registerGltfSkinJointSanitizer(duplicateGltfLoader);
  if (DRACOLoader) {
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(
      'https://www.gstatic.com/draco/versioned/decoders/1.5.6/'
    );
    duplicateGltfLoader.setDRACOLoader(dracoLoader);
  }
  duplicateGltfLoader.load(
    loadSource.url,
    function (gltf) {
      if (loadSource.revoke) {
        URL.revokeObjectURL(loadSource.url);
      }

      const sourceMesh = sourcePivot;
      const pivotPosition = sourceMesh
        ? {
            x: sourceMesh.position.x,
            y: sourceMesh.position.y,
            z: sourceMesh.position.z,
          }
        : {
            x: item.position_x || 0,
            y: item.position_y || 0,
            z: item.position_z || 0,
          };
      const pivotRotation = sourceMesh
        ? sourceMesh.rotation.clone()
        : new Three.Euler(
            Three.MathUtils.degToRad(item.rotation_x || 0),
            Three.MathUtils.degToRad(item.rotation_y || 0),
            Three.MathUtils.degToRad(item.rotation_z || 0)
          );
      const pivotScale = sourceMesh
        ? sourceMesh.scale.clone()
        : new Three.Vector3(
            item.scale_x || 1,
            item.scale_y || 1,
            item.scale_z || 1
          );

      const { pivot: duplicatedMesh, model: gltfModel } =
        createGlbPivotFromGltfScene(gltf, Three, {
          pivotId: tempId,
          position: pivotPosition,
          rotation: pivotRotation,
          scale: pivotScale,
          isAnimationActive: sourceMesh?.isAnimationActive,
          preserveAuthoredPivot: isPivotEditedAsset(item, sourceMesh),
        });

      applyDuplicateGlbMaterials({
        gltfModel,
        pivot: duplicatedMesh,
        sourcePivot: sourceMesh,
        item,
        Three,
      });

      const isShapeGlb =
        item.glb_type === 'shapes' ||
        sourceMesh?.glb_type === 'shapes' ||
        sourceMesh?.isShapes;

      duplicatedMesh.userData = {
        ...(duplicatedMesh.userData || {}),
        material_settings: cloneMaterialSettingsForDuplicate(
          sourceMesh,
          duplicatedMesh.userData?.material_settings ||
            sourceMesh?.userData?.material_settings ||
            item.material_settings ||
            {}
        ),
        project: item.project,
        user: item.user,
        asset_file_name: item.asset_file_name,
        file_name: duplicateFileName,
        asset_file_type: item.asset_file_type,
        asset_thumbnail: item.asset_thumbnail,
        position_x: duplicatedMesh.position.x,
        position_y: duplicatedMesh.position.y,
        position_z: duplicatedMesh.position.z,
        scale_x: duplicatedMesh.scale.x,
        scale_y: duplicatedMesh.scale.y,
        scale_z: duplicatedMesh.scale.z,
        rotation_x: item.rotation_x || 0,
        rotation_y: item.rotation_y || 0,
        rotation_z: item.rotation_z || 0,
        saved: false,
        mesh_code: isShapeGlb
          ? item.mesh_code || sourceMesh?.mesh_code
          : duplicateMeshCode,
        originalId: item._id || item.id,
        isDuplicate: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      attachClonedEventsToDuplicate(duplicatedMesh, {
        sourceItem: item,
        sourceObject: sourceMesh,
        events: item.events || sourceMesh?.events || sourceMesh?.userData?.events,
      });

      if (isShapeGlb) {
        applyShapeMetadataToDuplicate(
          duplicatedMesh,
          sourceMesh,
          item,
          duplicateFileName
        );
      }

      applyDuplicateMaterialToObject(duplicatedMesh, sourceMesh || duplicatedMesh);
      isolateDuplicatePublishIdentity(duplicatedMesh);

      // Handle pending uploads if needed
      if (setPendingUploads && originalMesh?.originalFile) {
        setPendingUploads((prev) => [
          ...prev,
          {
            file: originalMesh.originalFile,
            transform: {
              position_x: duplicatedMesh.position.x,
              position_y: duplicatedMesh.position.y,
              position_z: duplicatedMesh.position.z,
              rotation_x: item.rotation_x || 0,
              rotation_y: item.rotation_y || 0,
              rotation_z: item.rotation_z || 0,
              scale_x: duplicatedMesh.scale.x,
              scale_y: duplicatedMesh.scale.y,
              scale_z: duplicatedMesh.scale.z,
            },
            tempId: tempId,
            itemData: duplicatedMesh.userData, // Include all item data
          },
        ]);
      }
      duplicatedMesh.glb_type = item.glb_type;
      duplicatedMesh.file_name = duplicateFileName;
      duplicatedMesh.temp_file_name = tempId;
      // Preserve the original .glb URL so GLB child detection works in AppLeftSection
      // (extraction checks asset_file_path to identify .glb meshes for hierarchy extraction).
      if (item.asset_file_path && typeof item.asset_file_path === 'string' && item.asset_file_path.toLowerCase().includes('.glb')) {
        duplicatedMesh.asset_file_path = item.asset_file_path;
      }
      if (!attachToParent) {
        duplicatedMesh.position.x += 0.5;
      }
      isolateGlbAnimationMixers(duplicatedMesh);
      stopPlayModelForObject(duplicatedMesh);

      const nestedChildrenGlb = sourceMesh
        ? getNestedSceneMeshChildren(sourceMesh, scene)
        : [];
      syncNestedChildrenSceneParent(sourceMesh, nestedChildrenGlb);
      const childLocalTransformMapGlb = new Map(
        nestedChildrenGlb.map((child) => [
          child.uuid,
          captureChildLocalTransform(child, sourceMesh, Three),
        ])
      );

      if (attachToParent) {
        applyDuplicateUnderParent(
          attachToParent,
          duplicatedMesh,
          localTransform ||
            captureChildLocalTransform(
              sourceMesh,
              sourceMesh?.parent,
              Three
            )
        );
      } else {
        scene.add(duplicatedMesh);
      }

      // Update Redux store
      store.dispatch(setPushIntersectedObject(duplicatedMesh));
      store.dispatch(addSceneMesh(duplicatedMesh));
      store.dispatch(
        addModel({ url: item.asset_file_path, name: duplicateFileName })
      );

      if (!attachToParent && nestedChildrenGlb.length > 0) {
        duplicateNestedMeshChildren(
          duplicatedMesh,
          nestedChildrenGlb,
          {
            Three,
            scene,
            transformControls,
            selectedObject,
            GLTFLoader,
            DRACOLoader,
            SplatLoader,
            setPolyCount,
          },
          setPendingUploads,
          sourceMesh,
          childLocalTransformMapGlb
        );
      }

      // // Show success message
      // toast.success(
      //   `GLB file duplicated successfully as "${duplicateFileName}"`
      // );
      const displayName =
        tempId.length > 30 ? `${tempId.substring(0, 30)}...` : tempId;
      toast.success(`Element duplicated as "${displayName}"`);

      // Update Redux store with new asset - include all original properties
      const duplicateItemData = {
        ...item,
        _id: tempId,
        id: tempId,
        temp_file_name: tempId,
        file_name: duplicateFileName,
        mesh_code: isShapeGlb
          ? item.mesh_code || sourceMesh?.mesh_code
          : duplicateMeshCode,
        glb_type: item.glb_type,
        shape_type: isShapeGlb
          ? item.mesh_code || sourceMesh?.mesh_code
          : item.shape_type,
        position_x: duplicatedMesh.position.x,
        position_y: duplicatedMesh.position.y,
        position_z: duplicatedMesh.position.z,
        parent: attachToParent
          ? attachToParent._id || attachToParent.name || attachToParent.url
          : item.parent,
        events:
          duplicatedMesh.events ||
          duplicatedMesh.userData?.events ||
          [],
        saved: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        originalId: item._id || item.id,
        isDuplicate: true,
      };

      store.dispatch(
        add_new_assets({
          type: 'mesh',
          data: duplicatedMesh,
          itemData: duplicateItemData,
        })
      );
      if (attachToParent) {
        const parentId =
          attachToParent._id || attachToParent.name || attachToParent.url;
        setMeshAssetParentInRedux(tempId, parentId);
      }
      store.dispatch(set_is_glb_image_editoropen(true));
      store.dispatch(selected_object(duplicatedMesh));
      store.dispatch(setLoadingMessage(false));
    },
    undefined,
    function (error) {
      console.error('Error duplicating GLB:', error);
      toast.error('Failed to duplicate GLB asset');
      store.dispatch(setLoadingMessage(false));
    }
  );
}
